import { Link, useNavigate } from 'react-router-dom';
import Apertavel from '@/components/movimento/Apertavel';
import {
  type ItemVitrine,
  dataCurta,
  estadoDaLista,
  fichasAPartirTexto,
  linhaDeUrgencia,
  linkDaAcao,
  linkDoEvento,
  linkWhatsApp,
  linkWhatsAppContato,
  precoDaLista,
  rotuloAcao,
  seloData,
  vendeFichas,
  vendePeloWhatsApp,
} from '@/pages/home/vitrineVenda';
import { WHATSAPP_URL } from '@/pages/conheca/conteudo';
import CartazEvento from '@/pages/home/components/CartazEvento';

interface VendaProps {
  item: ItemVitrine;
  comTaxa?: boolean;
  prioridade?: boolean;
}

// Cartão grande da coluna "À venda agora": cartaz inteiro em cima, selo com a
// data, nome, preço, situação e os dois botões (principal + WhatsApp).
export function CartaoVenda({ item, comTaxa = false, prioridade = false }: VendaProps) {
  const navigate = useNavigate();
  const selo = seloData(item.inicio_em);
  const onda = [dataCurta(item.inicio_em), item.cidade].filter(Boolean).join(' · ');
  const preco = precoDaLista(item);
  const comFichas = vendeFichas(item);
  const fichas = comFichas ? fichasAPartirTexto(item) : null;
  const urgencia = linhaDeUrgencia(item);
  const zapDireto = vendePeloWhatsApp(item);
  const link = linkDoEvento(item);

  return (
    <article className="overflow-hidden rounded-[16px] bg-background-50 shadow-pcard">
      <Apertavel
        onClick={() => navigate(link)}
        aria-label={`Abrir ${item.nome}`}
        className="block w-full cursor-pointer text-left"
      >
        <div className="relative w-full bg-background-100">
          <CartazEvento
            mobile={item.imagem_mobile_url}
            desktop={item.imagem_desktop_url}
            alt={item.nome}
            dia={selo.dia}
            mes={selo.mes}
            carregarJa={prioridade}
            tamanho="grande"
            ajuste="natural"
          />

          {selo.dia && (
            <div className="absolute left-3 top-3 flex w-[46px] flex-col items-center rounded-[10px] bg-white px-1.5 py-1 leading-none">
              <span className="text-[17px] font-extrabold text-foreground-950">{selo.dia}</span>
              <span className="mt-0.5 text-[9.5px] font-bold uppercase tracking-[0.08em] text-primary-600">
                {selo.mes}
              </span>
            </div>
          )}

          <span
            className={`absolute right-3 top-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wide text-white ${
              zapDireto ? 'bg-[#128C4A]' : 'bg-accent-500'
            }`}
          >
            <i className={zapDireto ? 'ri-whatsapp-line' : 'ri-flashlight-fill'} aria-hidden />
            {zapDireto ? 'Pelo WhatsApp' : comFichas ? 'Fichas à venda' : 'À venda'}
          </span>
        </div>
      </Apertavel>

      <div className="p-4">
        <Apertavel
          onClick={() => navigate(link)}
          className="block w-full cursor-pointer text-left"
        >
          <h3 className="line-clamp-2 text-[20px] font-extrabold leading-tight text-foreground-950">
            {item.nome}
          </h3>
        </Apertavel>
        <p className="mt-1 truncate text-[13px] font-medium text-foreground-600">{onda}</p>

        {fichas ? (
          <p className="mt-2 text-[15px] font-extrabold text-foreground-950">{fichas}</p>
        ) : preco ? (
          <p className="mt-2 text-[15px] font-extrabold text-foreground-950">
            {preco}
            {comTaxa && <span className="ml-1 text-[11px] font-semibold text-foreground-500">+ taxa</span>}
          </p>
        ) : (
          <p className="mt-2 text-[14px] font-bold text-foreground-700">
            {comFichas ? 'Fichas à venda' : estadoDaLista(item)}
          </p>
        )}

        {urgencia && (
          <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-accent-100 px-2 py-0.5 text-[11px] font-bold text-accent-900">
            <i className="ri-time-line text-[12px]" aria-hidden />
            {urgencia}
          </span>
        )}

        <div className="mt-3 flex items-center gap-2">
          {zapDireto ? (
            <a
              href={linkWhatsAppContato(item)}
              target="_blank"
              rel="noopener noreferrer"
              className="aperta flex h-11 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-[12px] bg-[#128C4A] px-4 text-[14px] font-bold text-white transition-colors hover:bg-[#0F7440]"
            >
              <i className="ri-whatsapp-line text-[17px]" aria-hidden />
              {rotuloAcao(item)}
            </a>
          ) : (
            <Apertavel
              onClick={() => navigate(linkDaAcao(item))}
              className="flex h-11 flex-1 cursor-pointer items-center justify-center whitespace-nowrap rounded-[12px] bg-primary-500 px-4 text-[14px] font-bold text-white transition-colors hover:bg-primary-600"
            >
              {rotuloAcao(item)}
            </Apertavel>
          )}
          <a
            href={linkWhatsApp(item)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Enviar ${item.nome} no WhatsApp`}
            className="aperta flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-[#128C4A] transition-colors hover:bg-background-100"
          >
            <i className="ri-whatsapp-line text-[20px]" aria-hidden />
          </a>
        </div>
      </div>
    </article>
  );
}

interface BreveProps {
  item: ItemVitrine;
}

// Cartão menor de "Em breve": cartaz à esquerda e os botões à direita.
export function CartaoEmBreve({ item }: BreveProps) {
  const navigate = useNavigate();
  const selo = seloData(item.inicio_em);
  const onde = [dataCurta(item.inicio_em), item.cidade].filter(Boolean).join(' · ');
  const zapDireto = vendePeloWhatsApp(item);
  const link = linkDoEvento(item);

  return (
    <article className="flex gap-3 rounded-[16px] bg-background-50 p-2.5 shadow-pcard">
      <Apertavel
        onClick={() => navigate(link)}
        aria-label={`Abrir ${item.nome}`}
        tabIndex={-1}
        className="block h-[104px] w-[82px] shrink-0 cursor-pointer overflow-hidden rounded-[10px] bg-background-100"
      >
        <CartazEvento
          mobile={item.imagem_mobile_url}
          desktop={item.imagem_desktop_url}
          alt=""
          dia={selo.dia}
          mes={selo.mes}
          tamanho="pequeno"
          ajuste="conter"
        />
      </Apertavel>

      <div className="flex min-w-0 flex-1 flex-col">
        <Apertavel
          onClick={() => navigate(link)}
          className="block w-full cursor-pointer text-left"
        >
          <h3 className="line-clamp-2 text-[15px] font-bold leading-snug text-foreground-950">
            {item.nome}
          </h3>
        </Apertavel>
        <p className="mt-0.5 truncate text-[12px] text-foreground-600">{onde}</p>
        <p className="mt-0.5 line-clamp-1 text-[12px] font-semibold text-foreground-700">
          {estadoDaLista(item)}
        </p>

        <div className="mt-auto flex items-center justify-end gap-1.5 pt-2">
          <a
            href={linkWhatsApp(item)}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Enviar ${item.nome} no WhatsApp`}
            className="aperta flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-background-300 bg-background-50 text-[#128C4A]"
          >
            <i className="ri-whatsapp-line text-[17px]" aria-hidden />
          </a>

          {zapDireto ? (
            <a
              href={linkWhatsAppContato(item)}
              target="_blank"
              rel="noopener noreferrer"
              className="aperta flex h-9 shrink-0 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-full bg-[#128C4A] px-3 text-[12.5px] font-bold text-white"
            >
              <i className="ri-whatsapp-line text-[15px]" aria-hidden />
              Chamar no WhatsApp
            </a>
          ) : (
            <Apertavel
              onClick={() => navigate(link)}
              className="flex h-9 shrink-0 cursor-pointer items-center justify-center whitespace-nowrap rounded-full border border-primary-200 bg-primary-50 px-3.5 text-[12.5px] font-bold text-primary-700"
            >
              Me avise
            </Apertavel>
          )}
        </div>
      </div>
    </article>
  );
}

// Rodapé fino da inicial: só o essencial, tudo em uma linha que quebra.
export function RodapeInicio() {
  const item = 'text-[12px] font-semibold text-foreground-600 transition-colors hover:text-primary-700';
  return (
    <footer className="mx-auto mt-6 w-full max-w-[720px] border-t border-background-200 px-4 py-5">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={`${item} inline-flex items-center gap-1.5 whitespace-nowrap`}
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-whatsapp-line" aria-hidden />
          </span>
          WhatsApp do Se Liga
        </a>
        <span className="text-foreground-300" aria-hidden>
          ·
        </span>
        <Link to="/instalar" className={`${item} whitespace-nowrap`}>
          Colocar na tela do celular
        </Link>
        <span className="text-foreground-300" aria-hidden>
          ·
        </span>
        <Link to="/privacidade" className={`${item} whitespace-nowrap`}>
          Privacidade
        </Link>
        <span className="text-foreground-300" aria-hidden>
          ·
        </span>
        <Link to="/termos" className={`${item} whitespace-nowrap`}>
          Termos
        </Link>
      </div>
    </footer>
  );
}